import React, { useState, useLayoutEffect, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import PageTitle from '@/components/common/PageTitle';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Messages } from '@/common/constants';
import Loading from '@/components/common/Loading';
import { ExportHistory } from '@/types/History.type';
import DepartmentService from '@/services/Department.service';
import { Department } from '@/types/Department.type';
import Employee from '@/types/Employee.type';
import EmployeeService from '@/services/Employee.service';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import dayjs from 'dayjs';
import isAfter from 'dayjs/plugin/isSameOrAfter';
import HistoryService from '@/services/History.service';
import fileDownload from 'js-file-download';
import { Button, FormHelperText, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { Role } from '@/common/constants';
import { useAuth } from '@/common/hooks/useAuth';

const HistoryExport: React.FC = () => {
  const useFormMethods = useForm<ExportHistory>({
    defaultValues: {
      loginFlg: true,
      statusFlg: true,
    },
    shouldUnregister: false,
  });
  const {
    handleSubmit,
    getValues,
    watch,
    setValue,
    reset,
    formState: { isSubmitting },
  } = useFormMethods;
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState<Department[] | []>([]);
  const [employee, setEmployee] = useState<Employee[] | []>([]);
  const [error, seteError] = useState(false);

  const departmentId: HookFormControlsProps = {
    name: 'departmentId',
    type: 'autocomplete',
    label: '部署',
    options: department,
    getOptionLabel: 'name',
    autocompleteProps: {
      multiple: false,
    },
  };

  const employeeId: HookFormControlsProps = {
    name: 'employeeId',
    type: 'autocomplete',
    label: '社員',
    options: employee,
    getOptionLabel: 'name',
    autocompleteProps: {
      multiple: false,
    },
  };

  const createdAtFrom: HookFormControlsProps = {
    name: 'createdAtFrom',
    type: 'datetime-local',
    label: '期間開始',
    InputLabelProps: { shrink: true },
    rule: {
      pattern: {
        value: new RegExp(
          '^[0-9]{4}(-|/)(0[1-9]|1[0-2])(-|/)(0[1-9]|[12][0-9]|3[01])|([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$',
        ),
        message: '日付の形式が不正です。',
      },
      validate: (value) =>
        !value ||
        dayjs(value).isAfter(dayjs().subtract(3, 'month'), 'day') ||
        '期間開始は本日より3か月以内の日付を入力してください。',
    },
  };
  const createdAtTo: HookFormControlsProps = {
    name: 'createdAtTo',
    type: 'datetime-local',
    label: '期間終了',
    InputLabelProps: { shrink: true },
    rule: {
      pattern: {
        value: new RegExp(
          '^[0-9]{4}(-|/)(0[1-9]|1[0-2])(-|/)(0[1-9]|[12][0-9]|3[01])|([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$',
        ),
        message: '日付の形式が不正です。',
      },
      validate: (value) =>
        !value || value >= getValues('createdAtFrom')! || '期間終了には、期間開始以降の日付を指定してください。',
    },
  };

  const loginFlg: HookFormControlsProps = {
    name: 'loginFlg',
    type: 'checkbox',
    label: 'ログイン/ログアウト',
  };

  const statusFlg: HookFormControlsProps = {
    name: 'statusFlg',
    type: 'checkbox',
    label: 'ステータス変更',
  };

  useLayoutEffect(() => {
    (async () => {
      await DepartmentService.getAll().then((response: { data: Department[] }) => {
        setDepartment(response.data);
      });
      await EmployeeService.getAll().then((response: { data: Employee[] }) => {
        setEmployee(response.data);
      });
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    checkBoxErrer();
  }, [watch('loginFlg'), watch('statusFlg')]);

  useEffect(() => {
    (async () => {
      const department = getValues('departmentId') as Department | undefined;
      if (department?.id) {
        await DepartmentService.getEmployees(department.id).then((response: { data: Employee[] }) => {
          setEmployee(response.data);
        });
      } else {
        await EmployeeService.getAll().then((response: { data: Employee[] }) => {
          setEmployee(response.data);
        });
      }
      setValue('employeeId', null);
      setLoading(false);
    })();
  }, [watch('departmentId')]);

  const onSubmit = async (values: any) => {
    if (checkBoxErrer()) {
      try {
        const requestValues = {
          responseType: 'blob',
          params: {
            createdAtFrom: values.createdAtFrom ? dayjs(values.createdAtFrom).format('YYYY-MM-DD HH:mm:ss') : null,
            createdAtTo: values.createdAtTo ? dayjs(values.createdAtTo).format('YYYY-MM-DD HH:mm:ss') : null,
            departmentId: values.departmentId ? values.departmentId.id : null,
            employeeId: values.employeeId ? values.employeeId.id : null,
            loginFlg: values.loginFlg ? 1 : 0,
            statusFlg: values.statusFlg ? 1 : 0,
          },
        };

        await HistoryService.export(requestValues).then((res) => {
          if (res.data.size) {
            fileDownload(res.data, '操作履歴_' + dayjs().format('YYYYMMDDHHmmss') + '.csv');
            enqueueSnackbar('CSV出力が完了しました。', { variant: 'info' });
            return;
          }
          enqueueSnackbar('条件に一致する履歴は存在しません。', { variant: 'info' });
        });
      } catch (error: any) {
        enqueueSnackbar(error.response);
        return;
      }
    }
  };

  const checkBoxErrer = () => {
    if (!getValues('loginFlg') && !getValues('statusFlg')) {
      seteError(true);
      return false;
    }
    seteError(false);
    return true;
  };

  const resetSearchConditions = (value: boolean) => {
    reset();
    operationItemSelect(value);
  };

  const operationItemSelect = (value: boolean) => {
    setValue('loginFlg', value);
    setValue('statusFlg', value);
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={8}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>操作履歴</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography>ログイン、ログアウト、ステータス変更の履歴をCSV出力します。</Typography>
                  <Typography>出力できる履歴は過去3か月分になります。</Typography>
                </Grid>
                <Grid item xs={12} display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    sx={{ minWidth: '6.5rem' }}
                    onClick={() => resetSearchConditions(true)}
                  >
                    条件クリア
                  </Button>
                </Grid>
                <Grid item xs={12} display="flex" gap={2}>
                  <HookFormControls {...createdAtFrom} />

                  <HookFormControls {...createdAtTo} />
                </Grid>
                {auth.user.role === Role.管理者 && (
                  <Grid item xs={12} display="flex" gap={2}>
                    <HookFormControls {...departmentId} />
                    <HookFormControls {...employeeId} />
                  </Grid>
                )}
                <Grid container item xs={12}>
                  <Grid item xs={12} display="flex" gap={2}>
                    <Typography>操作項目</Typography>
                    <Button
                      variant="outlined"
                      sx={{ minWidth: '6.5rem' }}
                      onClick={() => operationItemSelect(true)}
                    >
                      すべて選択
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ minWidth: '6.5rem' }}
                      onClick={() => operationItemSelect(false)}
                    >
                      すべて解除
                    </Button>
                  </Grid>
                  <Grid item xs={12} display="flex" gap={2}>
                    <HookFormControls {...loginFlg} />
                    <HookFormControls {...statusFlg} />
                  </Grid>
                  {error && <FormHelperText error>操作項目は1つ以上選択してください。</FormHelperText>}
                </Grid>
                <Grid item xs={12} display="flex" gap={2} justifyContent="flex-end">
                  <LoadingButton type="submit" variant="contained" sx={{ mt: 3, mb: 2 }} loading={isSubmitting}>
                    CSV出力
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default HistoryExport;
